import React, { useState, useEffect } from 'react'
import './userCenter.css'
import { useNavigate } from 'react-router-dom'
import homeIcon from '../assets/icon/homeIcon.png'
import activationIcon from '../assets/icon/cardActivationIcon.png'
import historyIcon from '../assets/icon/cardDetectionIcon.png'
import { getUserInfo, redeem } from '../functions'
import { getToken, getUsername } from '../utils/auth'
import { useLanguage } from '../contexts/LanguageContext'

interface UserInfo {
    username: string
    email: string
    coin: number
    vipLevel: number
    vipExpiry: string
    birthday?: string
    birthplace?: string
    birthtime?: string
    createTime?: string
    lastLoginTime?: string
}

const UserCenter = () => {
    const navigate = useNavigate()
    const { t } = useLanguage()
    const [redemptionCode, setRedemptionCode] = useState('')
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [redeemLoading, setRedeemLoading] = useState(false)
    const [redeemMessage, setRedeemMessage] = useState<{ type: 'success', text: string } | null>(null)
    const [redeemError, setRedeemError] = useState<string | null>(null)
    const [redeemPlaceholder, setRedeemPlaceholder] = useState('')

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setLoading(true)
                const token = getToken()
                const user = await getUserInfo(token || '')
                // console.log('用户信息:', user)
                setUserInfo(user)
            } catch (error) {
                console.error('获取用户信息失败:', error)
                // 如果获取失败，使用默认值
                setUserInfo({
                    username: '',
                    email: '',
                    coin: 0,
                    vipLevel: 0,
                    vipExpiry: '',
                    birthday: '',
                    birthplace: '',
                    birthtime: '',
                    createTime: '',
                    lastLoginTime: ''
                })
            } finally {
                setLoading(false)
            }
        }

        fetchUserInfo()
    }, [])

    // Initialize placeholder with translated text
    useEffect(() => {
        setRedeemPlaceholder(t.userCenterRedeemPlaceholder)
    }, [t])

    const handleBack = () => navigate(-1)
    const getVipTier = (level: number | undefined | null) => {
        switch (level) {
            case 1:
                return 'bronze'
            case 2:
                return 'silver'
            case 3:
                return 'gold'
            case -1:
                return 'Founder'
            default:
                return 'normal'
        }
    }
    const handleRedeem = async () => {
        if (!redemptionCode.trim()) {
            setRedeemError(t.userCenterRedeemCodeRequired)
            setRedeemPlaceholder(t.userCenterRedeemCodeRequired)
            setRedemptionCode('')
            return
        }

        const token = getToken()

        if (!token) {
            setRedeemError(t.userCenterPleaseLogin)
            setRedeemPlaceholder(t.userCenterPleaseLogin)
            setRedemptionCode('')
            return
        }

        setRedeemLoading(true)
        setRedeemMessage(null)
        setRedeemError(null)
        setRedeemPlaceholder(t.userCenterValidating)

        try {
            // 优先使用从 API 获取的 username，而不是 localStorage 中的值
            const username = userInfo?.username || ''
            if (!username) {
                setRedeemError('无法获取用户名，请刷新页面后重试')
                setRedeemPlaceholder('无法获取用户名')
                setRedeemLoading(false)
                return
            }
            const result = await redeem(token, redemptionCode.trim(), username)
            console.log('API兑换结果:', result)

            const coinAdded = result.coinAdded || 0;

            const rawNewVipLevel = result.newVipLevel
            let successMsg = "";

            if (coinAdded > 0) {
                successMsg = t.userCenterRedeemSuccess(coinAdded);
            }
            if (rawNewVipLevel) {
                const tier = getVipTier(rawNewVipLevel).toUpperCase();
                const vipMsg = t.userCenterRedeemSuccessVip(tier);

                if (successMsg) {
                    successMsg = `${successMsg}\n${vipMsg}`;
                } else {
                    successMsg = vipMsg;
                }
            }


            setRedeemMessage({
                type: 'success',
                text: successMsg
            })
            setRedeemPlaceholder(successMsg)

            // 直接根据 redeem 返回值更新余额和 VIP 信息
            if (userInfo) {
                // 检查多个可能的字段名
                const newVipLevel = result.newVipLevel ?? result.vipLevel ?? result.user?.vipLevel
                const newVipExpiry = result.newVipExpiry ?? result.vipExpiry ?? result.user?.vipExpiry

                setUserInfo({
                    ...userInfo,
                    coin: (userInfo.coin || 0) + (result.coinAdded || 0),
                    vipLevel: newVipLevel !== undefined ? newVipLevel : userInfo.vipLevel,
                    vipExpiry: newVipExpiry || userInfo.vipExpiry
                })
            } else {
                // 如果 userInfo 为空，重新获取用户信息
                const updatedUser = await getUserInfo(token)
                // console.log('更新后的用户信息:', updatedUser)
                setUserInfo({
                    username: updatedUser.username,
                    email: updatedUser.email || '',
                    coin: updatedUser.coin || 0,
                    vipLevel: updatedUser.vipLevel,
                    vipExpiry: updatedUser.vipExpiry,
                    birthday: updatedUser.birthday || '',
                    birthplace: updatedUser.birthplace || '',
                    birthtime: updatedUser.birthtime || '',
                    createTime: updatedUser.createTime || '',
                    lastLoginTime: updatedUser.lastLoginTime || ''
                })
            }
        } catch (error: any) {
            console.error('兑换失败:', error)
            setRedeemError(error.message || t.userCenterRedeemFailed)
            setRedeemPlaceholder(error.message || t.userCenterRedeemFailed)
        } finally {
            setRedeemLoading(false)
            setRedemptionCode('')
        }
    }
    const handleTokenHistory = () => navigate('/tokenHistory')
    const handleFortuneHistory = () => navigate('/fortuneHistory')
    const handleTopUp = () => navigate('/topup')
    const handleDevices = () => navigate('/deviceManage')
    const handleLogout = () => {
        // 清除 token 和用户信息
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        navigate('/authPage')
    }

    // 获取用户名首字母（用于头像）
    const getInitial = (name: string) => {
        return name ? name.charAt(0).toUpperCase() : 'T'
    }

    // 计算进度条百分比（基于余额，假设最大值为 10000）
    const getProgressPercentage = (coin: number) => {
        const maxCoin = 10000
        return Math.min((coin / maxCoin) * 100, 100)
    }

    useEffect(() => {
        if (userInfo) {
            // console.log('当前用户信息:', userInfo)
        }
    }, [userInfo])

    return (
        <div className="user-center-page">
            <div className="user-center-header-icons">
                <button className="user-center-icon-btn" onClick={handleBack} aria-label={t.userCenterBack}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>

            <div className="user-center-content">
                <div className="user-center-main-row">
                    <header className={`user-center-hero vip-${getVipTier(userInfo?.vipLevel)}`}>
                        <div className="hero-left">
                            <div className="hero-avatar">
                                <div className="avatar-inner">
                                    {loading ? '...' : getInitial(userInfo?.username || 'test-user')}
                                </div>
                            </div>
                            <div className="hero-info">
                                <h1 className="hero-name">
                                    {loading ? t.userCenterLoading : (userInfo?.username?.toUpperCase() || 'TEST-USER')}
                                    {!loading && userInfo?.vipLevel === -1 && (
                                        <span className="founder-badge">✦ Founder</span>
                                    )}
                                </h1>
                                <p className="hero-email">
                                    {loading ? t.userCenterLoading : (userInfo?.email || 'test-user@example.com')}
                                </p>
                                <div className="hero-divider"></div>
                                <div className="hero-balance-row">
                                    <span className="balance-label">{t.userCenterBalance}</span>
                                    <span className="balance-value">
                                        {loading ? '...' : `${userInfo?.coin || 0} DCOIN`}
                                    </span>
                                </div>
                                <div className="balance-progress">
                                    <div
                                        className="progress-fill"
                                        style={{
                                            width: loading
                                                ? '0%'
                                                : `${getProgressPercentage(userInfo?.coin || 0)}%`
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                        <div className="hero-redeem">
                            <input
                                type="text"
                                className={`redeem-input${redeemError ? ' redeem-input-error' : ''}`}
                                placeholder={redeemPlaceholder}
                                value={redemptionCode}
                                onChange={(e) => {
                                    setRedemptionCode(e.target.value)
                                    if (redeemMessage) setRedeemMessage(null)
                                    if (redeemError) setRedeemError(null)
                                    setRedeemPlaceholder(t.userCenterRedeemPlaceholder)
                                }}
                                onFocus={() => {
                                    if (redeemError) setRedeemError(null)
                                    setRedeemPlaceholder(t.userCenterRedeemPlaceholder)
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !redeemLoading) {
                                        handleRedeem()
                                    }
                                }}
                                disabled={redeemLoading}
                            />
                            <button
                                className="redeem-btn"
                                onClick={handleRedeem}
                                disabled={redeemLoading}
                            >
                                {redeemLoading ? t.userCenterRedeeming : t.userCenterRedeemNow}
                            </button>
                            {redeemMessage && (
                                <div style={{
                                    marginTop: '10px',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    backgroundColor: redeemMessage.type === 'success'
                                        ? 'rgba(124, 255, 161, 0.2)'
                                        : 'rgba(255, 138, 138, 0.2)',
                                    color: redeemMessage.type === 'success'
                                        ? '#7CFFA1'
                                        : '#ff8a8a',
                                    fontSize: '14px',
                                    textAlign: 'center'
                                }}>
                                    {redeemMessage.text}
                                </div>
                            )}
                        </div>
                    </header>

                    <button className="widget-card" onClick={handleTopUp}>
                        <div className="widget-icon">
                            <img src={activationIcon} alt="Recharge" />
                        </div>
                        <div className="widget-title">{t.userCenterRechargeCenter}</div>
                    </button>

                    <section className="user-center-actions">
                        <button className="action-button" onClick={handleLogout}>
                            <span className="action-button-label">{t.userCenterLogout}</span>
                        </button>
                    </section>
                </div>
            </div>
        </div>
    )
}

export default UserCenter
